import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import { NEUTRAL_COLOR, SECTOR_COLORS, SECTOR_LABELS } from "../acgSummary";
import { VERDICT_LABELS, waterObjective } from "../objectiveCurve";
import {
  CHART_INK,
  CHART_SURFACE,
  categoryAxis,
  legend,
  tooltip,
  valueAxis,
} from "./chartTheme";

// LineController EN PLUS des éléments : un jeu de données `type: "line"` posé
// dans un <Bar> est un graphe mixte, et chart.js refuse un contrôleur qu'on ne
// lui a pas enregistré (« "line" is not a registered controller »). Seul le
// visualiseur de territoire l'enregistrait — un atelier sans carte plantait.
ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineController, LineElement, PointElement,
  Title, Tooltip, Legend,
);

export const OBJECTIVE_LABEL = "Courbe d'objectif";

/**
 * LES ÉCONOMIES D'EAU, TOUR PAR TOUR ET PAR SECTEUR.
 *
 * Une barre empilée par tour. Le dessin suit `chartTheme` : pas de quadrillage
 * vertical, pas de trait d'axe, des repères horizontaux clairs — sur un écran
 * projeté, ce sont les hauteurs qu'on lit, jamais les graduations.
 *
 * LES SEGMENTS SONT ARRONDIS ET SÉPARÉS PAR DU BLANC (`borderColor` à la
 * couleur de la carte) : c'est ce qui distingue les secteurs d'un coup d'œil,
 * là où l'aplat continu obligeait à suivre la légende. Les couleurs et les noms
 * viennent d'acgSummary.js — le bilan de fin de séance affiche les mêmes
 * secteurs juste en dessous, et deux tables séparées finiraient par se
 * contredire.
 *
 * `showObjective` POSE LA COURBE D'OBJECTIF par-dessus les barres (calculs
 * dans objectiveCurve.js) et un badge « en avance / dans l'objectif / en
 * retard » à côté du titre. C'est le graphe d'ENTRE DEUX TOURS qui le demande
 * (pages de type `score`) ; le bilan de fin ne le fait pas. La courbe est dans
 * l'encre du graphe, en pointillés : aucun secteur ne porte cette couleur, et
 * c'est le badge qui porte le verdict, pas la ligne.
 */
const WaterHistogramChart = ({
  gameStatus,
  width = "100%",
  height = "320px",
  showObjective = false,
  targetPerRound,
}) => {
  const waterMetrics = gameStatus.score_dic["water_projection"]?.metadata;

  if (!waterMetrics || typeof waterMetrics !== "object") {
    return <div className="histogram-chart-error">Histogram data not found</div>;
  }

  const sectorColor = (key) => SECTOR_COLORS[key] || NEUTRAL_COLOR;
  const objective = showObjective ? waterObjective(gameStatus, { targetPerRound }) : null;

  // Suppose que tous les tableaux ont la même longueur
  const labels = waterMetrics[Object.keys(waterMetrics)[0]].map((_, i) => `Tour ${i + 1}`);

  const bars = Object.entries(waterMetrics).map(([key, values]) => ({
    label: SECTOR_LABELS[key] || key,
    data: values,
    backgroundColor: sectorColor(key),
    borderColor: CHART_SURFACE,
    borderWidth: 2,
    borderRadius: 5,
    borderSkipped: false,
    maxBarThickness: 54,
    categoryPercentage: 0.72,
    order: 1,
  }));

  // La courbe est PREMIÈRE dans la liste et d'ordre 0 : chart.js dessine les
  // jeux de données du dernier au premier, c'est ce qui la met au-dessus des
  // barres. Elle a son propre empilement (type différent), l'axe empilé ne
  // l'additionne à rien.
  const objectiveLine = objective && {
    type: "line",
    label: OBJECTIVE_LABEL,
    data: objective.curve,
    borderColor: CHART_INK,
    backgroundColor: CHART_INK,
    borderWidth: 2.5,
    borderDash: [7, 5],
    pointRadius: 5,
    pointHoverRadius: 7,
    pointBorderColor: CHART_SURFACE,
    pointBorderWidth: 2,
    tension: 0.3,
    fill: false,
    order: 0,
  };

  const data = { labels, datasets: objectiveLine ? [objectiveLine, ...bars] : bars };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    // Survoler un tour donne les quatre secteurs à la fois : c'est la question
    // qu'on pose à ce graphe, pas la valeur d'un segment isolé.
    interaction: { mode: "index", intersect: false },
    plugins: {
      // L'objectif se lit en dernier dans la légende, après les secteurs
      // qu'il commente — quel que soit l'ordre de dessin.
      legend: legend({
        labels: {
          ...legend().labels,
          sort: (a, b) =>
            (a.text === OBJECTIVE_LABEL) - (b.text === OBJECTIVE_LABEL) ||
            a.datasetIndex - b.datasetIndex,
        },
      }),
      tooltip: tooltip(),
      title: { display: false },
      datalabels: { display: false },
    },
    scales: {
      // Les libellés disent « Tour 1 », « Tour 2 » : un titre d'axe « Tours »
      // ne fait que répéter. Le titre de la carte porte l'unité.
      x: categoryAxis({ stacked: true }),
      y: valueAxis({ stacked: true }),
    },
  };

  return (
    <div className="histogram-chart-container">
      <div className="histogram-chart-header">
        <div className="histogram-chart-title">Projection des économies d'eau</div>
        {objective && objective.verdict && (
          <span
            className={`objective-badge objective-badge--${objective.verdict}`}
            title={`Tour ${objective.roundsPlayed} sur ${objective.rounds} : ${objective.value} points à terme pour ${objective.target} visés`}
          >
            {VERDICT_LABELS[objective.verdict]}
          </span>
        )}
      </div>
      {/* La toile a sa propre boîte, comme dans RadarChart : chart.js se cale
          sur le parent du canvas, et le titre lui volait sa hauteur — l'axe des
          tours passait sous le bord de la carte. */}
      <div style={{ width, height }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

export default WaterHistogramChart;
